// redux/vaultQuestion/vaultQuestionSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllVaultQuestions,
  fetchVaultQuestionById,
  createVaultQuestionThunk,
  updateVaultQuestionThunk,
  deleteVaultQuestionById,
  toggleVaultQuestionStatus,
  flagVaultQuestionThunk,
  deleteVaultQuestionsBulkByIds,
  fetchVaultQuestionsExport,
  saveBulkVaultQuestions,
  fetchBulkVaultQuestions,
  fetchAiReview,
  bulkReviewThunk,
  publishQuestion,
  bulkPublishQuestions,
} from "./vaultQuestionThunks";

const initialState = {
  questionData: null,
  questionList: [],
  totalQuestions: 0,
  totalPages: 1,
  loading: false,
  error: null,
  exportLoading: false,
  exportError: null,
  parsedRows: [], // parsed rows from file
  parseErrors: [], // parsing errors
  validateErrors: [], // validation errors {line, messages[]}
  saveResult: null,
  saveError: null,
  bulkEditData: [], // store fetched bulk data
  reviewData: null,
  aiReviewLoading: false,
  bulkReviewResults: [],
  listRequestId: null, // the list fetch whose answer is shown (latest wins)
  filters: {
    currentPage: 1,
    itemsPerPage: 10,
    searchQuery: "",
    sortColumn: "createdAt",
    sortOrder: "desc",
    statusFilter: "",
    tierFilter: "", // For "iceBreaker", "deeperDrive", etc.
    reviewStatusFilter: "all", // reviewStatus field
    isPublishedFilter: "all", // true/false/all
    isActiveFilter: "all", // true/false/all
  },
};

const vaultQuestionSlice = createSlice({
  name: "vaultQuestion",
  initialState,
  reducers: {
    clearQuestionData: (state) => {
      state.questionData = null;
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setParsedRows(state, action) {
      state.parsedRows = action.payload;
    },
    setParseErrors(state, action) {
      state.parseErrors = action.payload;
    },
    setValidateErrors(state, action) {
      state.validateErrors = action.payload;
    },
    resetBulkUpload(state) {
      state.parsedRows = [];
      state.parseErrors = [];
      state.validateErrors = [];
      state.loading = false;
      state.saveResult = null;
      state.saveError = null;
    },
    updateRow(state, action) {
      const { index, row } = action.payload;
      // state.parsedRows[index] = row;
      state[index] = { ...state[index], ...row };
    },
    clearAiReview: (state) => {
      state.reviewData = null;
      state.error = null;
    },
    resetBulkReview: (state) => {
      state.bulkReviewResults = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVaultQuestionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVaultQuestionById.fulfilled, (state, action) => {
        state.questionData = action.payload;
        state.loading = false;
      })
      .addCase(fetchVaultQuestionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔴 THE LATEST REQUEST WINS (2026-11-03). Opening the list from a
      // link (the bell's member questions, the Dashboard card) fetches
      // twice: once with the filters already in the store, then with the
      // address's. An answer to an older request is dropped, so the rows
      // always fit the filters on screen.
      .addCase(fetchAllVaultQuestions.pending, (state, action) => {
        state.loading = true;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchAllVaultQuestions.fulfilled, (state, action) => {
        if (state.listRequestId && action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.questionList = action.payload.questions || [];
        state.totalQuestions = action.payload.metadata.total || 0;
        state.totalPages = action.payload.metadata.totalPages || 1;
      })
      .addCase(fetchAllVaultQuestions.rejected, (state, action) => {
        if (state.listRequestId && action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createVaultQuestionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(createVaultQuestionThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createVaultQuestionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateVaultQuestionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateVaultQuestionThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateVaultQuestionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(toggleVaultQuestionStatus.fulfilled, (state, action) => {
        const updatedQuestion = action.payload.question; // full question from backend
        const index = state.questionList.findIndex(
          (q) => q._id === updatedQuestion._id
        );
        // Merged, not replaced (2026-11-04): the stored row has none of the
        // list's own fields (who asked, and when it is shown), so a member
        // row keeps "Shows tonight in Ottawa" after its pill is clicked.
        if (index !== -1) state.questionList[index] = { ...state.questionList[index], ...updatedQuestion };
      })
      .addCase(toggleVaultQuestionStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteVaultQuestionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteVaultQuestionById.fulfilled, (state, action) => {
        state.questionList = state.questionList.filter(
          (q) => q._id !== action.payload
        );
        state.loading = false;
      })
      .addCase(deleteVaultQuestionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteVaultQuestionsBulkByIds.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteVaultQuestionsBulkByIds.fulfilled, (state, action) => {
        state.questionList = state.questionList.filter(
          (q) => !action.payload.includes(q._id)
        );
        state.loading = false;
      })

      .addCase(deleteVaultQuestionsBulkByIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(flagVaultQuestionThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchVaultQuestionsExport.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(fetchVaultQuestionsExport.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(fetchVaultQuestionsExport.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      })

      .addCase(saveBulkVaultQuestions.pending, (state) => {
        state.loading = true;
        state.saveError = null;
      })
      .addCase(saveBulkVaultQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.saveResult = action.payload;
      })
      .addCase(saveBulkVaultQuestions.rejected, (state, action) => {
        state.loading = false;
        state.saveError = action.payload;
      })
      .addCase(fetchBulkVaultQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBulkVaultQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkEditData = action.payload || [];
      })
      .addCase(fetchBulkVaultQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAiReview.pending, (state) => {
        state.aiReviewLoading = true;
        state.error = null;
      })
      .addCase(fetchAiReview.fulfilled, (state, action) => {
        state.aiReviewLoading = false;

        // Correct key from API
        const updatedDoc = action.payload.updated;

        state.reviewData = action.payload;

        if (updatedDoc) {
          // Update the question in the list
          const index = state.questionList.findIndex(
            (q) => q._id === updatedDoc._id
          );
          if (index !== -1) {
            state.questionList[index] = {
              ...state.questionList[index],
              ...updatedDoc,
            };
          }
        }
      })
      .addCase(fetchAiReview.rejected, (state, action) => {
        state.aiReviewLoading = false;
        state.error = action.payload;
      })
      .addCase(bulkReviewThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkReviewThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkReviewResults = action.payload.results || [];
      })
      .addCase(bulkReviewThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Bulk review failed";
      })
      .addCase(publishQuestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(publishQuestion.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.updated;
        if (updated) {
          const index = state.questionList.findIndex(
            (q) => q._id === updated._id
          );
          if (index !== -1)
            state.questionList[index] = {
              ...state.questionList[index],
              ...updated,
            };
        }
      })
      .addCase(publishQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add inside extraReducers builder
      .addCase(bulkPublishQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkPublishQuestions.fulfilled, (state, action) => {
        state.loading = false;

        const updatedQuestions = action.payload.updatedQuestions;
        if (updatedQuestions && updatedQuestions.length) {
          updatedQuestions.forEach((updated) => {
            const index = state.questionList.findIndex(
              (q) => q._id === updated._id
            );
            if (index !== -1) {
              state.questionList[index] = {
                ...state.questionList[index],
                ...updated,
              };
            }
          });
        }

        // optional toast info for skipped questions
        if (action.payload.skippedCount) {
          const skipped = action.payload.skippedQuestions.join(", ");
          console.warn(
            `Skipped ${action.payload.skippedCount} questions: ${skipped}`
          );
        }
      })
      .addCase(bulkPublishQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Bulk publish failed";
      });
  },
});

export const {
  clearQuestionData,
  setFilters,
  resetFilters,
  setParsedRows,
  setParseErrors,
  setValidateErrors,
  resetBulkUpload,
  updateRow,
  clearAiReview,
  resetBulkReview,
} = vaultQuestionSlice.actions;
export default vaultQuestionSlice.reducer;
